/**
 * @fileoverview Event Detail Dialog
 * @description Read-only detail view with action buttons (edit, extend, close, delete)
 */

import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Box, Chip, IconButton, Button, Divider,
  TextField, MenuItem, Alert, Grid,
} from '@mui/material';
import {
  X, Calendar, Clock, Users, Tag, MapPin, GitBranch,
  ExternalLink, Pencil, Trash2, ArrowRightLeft, Timer,
  CheckCircle,
} from 'lucide-react';
import { SubscribeButton } from '../SubscribeButton';
import { RequirePermission } from '@/components';
import { MODULES } from '@/constants';
import {
  EVENT_STATUS_LABELS, EVENT_STATUS_COLORS,
  PRIORITY_COLORS, STATUS_TRANSITIONS, EVENT_STATUSES,
} from '../../constants';
import { formatDisplay, formatDisplayDateTime, getDurationText } from '../../utils';

const InfoRow = ({ icon: Icon, label, value, isLink }) => {
  if (!value) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
      <Icon size={16} style={{ marginTop: 3, color: '#4B5563', flexShrink: 0 }} />
      <Box>
        <Typography variant="caption" sx={{ color: '#6B7280', display: 'block' }}>{label}</Typography>
        {isLink ? (
          <Typography
            variant="body2"
            component="a"
            href={value}
            target="_blank"
            rel="noopener"
            sx={{ color: '#01426A', textDecoration: 'underline' }}
          >
            {value}
          </Typography>
        ) : (
          <Typography variant="body2">{value}</Typography>
        )}
      </Box>
    </Box>
  );
};

const EventDetailDialog = ({
  open,
  onClose,
  event,
  isOwner,
  onEdit,
  onDelete,
  onChangeStatus,
  onExtend,
  onClose: onCloseEvent,
}) => {
  const [showExtend, setShowExtend] = useState(false);
  const [showClose, setShowClose] = useState(false);
  const [showStatusChange, setShowStatusChange] = useState(false);
  const [newEndDate, setNewEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [closeStatus, setCloseStatus] = useState('COMPLETED');
  const [newStatus, setNewStatus] = useState('');
  const [actionError, setActionError] = useState('');

  if (!event) return null;

  const statusColor = EVENT_STATUS_COLORS[event.status] || '#4A90D9';
  const priorityColor = PRIORITY_COLORS[event.priority] || '#4A90D9';
  const availableTransitions = STATUS_TRANSITIONS[event.status] || [];
  const isTerminal = availableTransitions.length === 0;

  const handleExtend = async () => {
    if (!newEndDate) {
      setActionError('New end date is required');
      return;
    }
    try {
      await onExtend(event.id, newEndDate, reason);
      setShowExtend(false);
      setNewEndDate('');
      setReason('');
      setActionError('');
      onClose();
    } catch (err) {
      setActionError(err.message);
    }
  };

  const handleClose = async () => {
    try {
      const closeEventFn = onCloseEvent || onChangeStatus;
      await closeEventFn(event.id, closeStatus, reason);
      setShowClose(false);
      setReason('');
      setActionError('');
      onClose();
    } catch (err) {
      setActionError(err.message);
    }
  };

  const handleStatusChange = async () => {
    if (!newStatus) return;
    try {
      await onChangeStatus(event.id, newStatus);
      setShowStatusChange(false);
      setNewStatus('');
      onClose();
    } catch (err) {
      setActionError(err.message);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', pb: 1 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: event.display_color || event.color_code || '#4A90D9', flexShrink: 0 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
              {event.title}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
            <Chip label={EVENT_STATUS_LABELS[event.status] || event.status} size="small"
              sx={{ backgroundColor: statusColor, color: '#fff', fontWeight: 600, fontSize: '0.7rem' }} />
            <Chip label={event.priority} size="small"
              sx={{ backgroundColor: priorityColor, color: '#fff', fontWeight: 600, fontSize: '0.7rem' }} />
            {event.team_name && <Chip label={event.team_name} size="small" variant="outlined" />}
            {event.test_type && <Chip label={event.test_type} size="small" variant="outlined" />}
          </Box>
          <Box sx={{ mt: 1.5 }}>
            <SubscribeButton eventId={event.id} />
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small"><X size={20} /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {actionError && <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert>}

        {event.description && (
          <Typography variant="body2" sx={{ mb: 2, color: '#4B5563' }}>
            {event.description}
          </Typography>
        )}

        <InfoRow icon={Calendar} label="Start Date" value={formatDisplayDateTime(event.start_date)} />
        <InfoRow icon={Calendar} label="End Date" value={formatDisplayDateTime(event.end_date)} />
        {event.original_end_date && (
          <InfoRow icon={Timer} label="Original End Date" value={formatDisplay(event.original_end_date)} />
        )}
        <InfoRow icon={Clock} label="Duration" value={getDurationText(event.start_date, event.end_date)} />

        <Divider sx={{ my: 2 }} />

        <InfoRow icon={MapPin} label="Environment" value={event.environment_name} />
        <InfoRow icon={Tag} label="Application" value={event.application_name} />
        <InfoRow icon={Tag} label="Module" value={event.module_name} />
        <InfoRow icon={Tag} label="Sprint" value={event.sprint_name} />
        <InfoRow icon={Tag} label="Release" value={event.release_version} />

        {/* Test Metrics */}
        {event.total_test_cases > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#01426A' }}>Test Metrics</Typography>
            <Grid container spacing={1}>
              {[
                { label: 'Total', value: event.total_test_cases },
                { label: 'Automated', value: event.automated_test_cases },
                { label: 'Manual', value: event.manual_test_cases },
                { label: 'Passed', value: event.passed_count, color: '#00AF66' },
                { label: 'Failed', value: event.failed_count, color: '#E31837' },
                { label: 'Blocked', value: event.blocked_count, color: '#F5A623' },
              ].map((m) => (
                <Grid size={4} key={m.label}>
                  <Box sx={{ textAlign: 'center', p: 1, backgroundColor: '#F9FAFB', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: m.color || '#1F2937' }}>
                      {m.value || 0}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>{m.label}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {/* External Links */}
        {(event.jira_ticket || event.ado_work_item_id || event.github_repo || event.pipeline_url) && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#01426A' }}>External Links</Typography>
            <InfoRow icon={ExternalLink} label="JIRA" value={event.jira_ticket} />
            <InfoRow icon={ExternalLink} label="ADO Work Item" value={event.ado_work_item_id} />
            <InfoRow icon={GitBranch} label="GitHub Repo" value={event.github_repo} />
            <InfoRow icon={ExternalLink} label="Pipeline" value={event.pipeline_url} isLink />
          </>
        )}

        {/* Tags */}
        {event.tags && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {event.tags.split(',').map((tag) => (
                <Chip key={tag.trim()} label={tag.trim()} size="small" variant="outlined" />
              ))}
            </Box>
          </>
        )}

        {/* Notes */}
        {event.notes && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 0.5, color: '#01426A' }}>Notes</Typography>
            <Typography variant="body2" sx={{ color: '#4B5563', whiteSpace: 'pre-wrap' }}>
              {event.notes}
            </Typography>
          </>
        )}

        {/* Inline: Extend Form */}
        {showExtend && (
          <Box sx={{ mt: 2, p: 2, backgroundColor: '#F0F7FF', borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Extend Event</Typography>
            <TextField
              label="New End Date"
              type="datetime-local"
              value={newEndDate}
              onChange={(e) => setNewEndDate(e.target.value)}
              fullWidth
              size="small"
              sx={{ mb: 1 }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="Reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              fullWidth
              size="small"
              sx={{ mb: 1 }}
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button size="small" onClick={() => setShowExtend(false)}>Cancel</Button>
              <Button size="small" variant="contained" onClick={handleExtend}>Extend</Button>
            </Box>
          </Box>
        )}

        {/* Inline: Close Form */}
        {showClose && (
          <Box sx={{ mt: 2, p: 2, backgroundColor: '#FFF5F5', borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Close Event</Typography>
            <TextField
              label="Close As"
              value={closeStatus}
              onChange={(e) => setCloseStatus(e.target.value)}
              fullWidth
              select
              size="small"
              sx={{ mb: 1 }}
            >
              <MenuItem value="COMPLETED">Completed</MenuItem>
              <MenuItem value="CLOSED_EARLY">Closed Early</MenuItem>
              <MenuItem value="CANCELLED">Cancelled</MenuItem>
            </TextField>
            <TextField
              label="Reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              fullWidth
              size="small"
              sx={{ mb: 1 }}
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button size="small" onClick={() => setShowClose(false)}>Cancel</Button>
              <Button size="small" variant="contained" color="error" onClick={handleClose}>Close Event</Button>
            </Box>
          </Box>
        )}

        {/* Inline: Status Change */}
        {showStatusChange && (
          <Box sx={{ mt: 2, p: 2, backgroundColor: '#F0FFF0', borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Change Status</Typography>
            <TextField
              label="New Status"
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              fullWidth
              select
              size="small"
              sx={{ mb: 1 }}
            >
              {availableTransitions.map((s) => (
                <MenuItem key={s} value={s}>{EVENT_STATUS_LABELS[s]}</MenuItem>
              ))}
            </TextField>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button size="small" onClick={() => setShowStatusChange(false)}>Cancel</Button>
              <Button size="small" variant="contained" onClick={handleStatusChange}>Update</Button>
            </Box>
          </Box>
        )}

        {/* Created/Updated info */}
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
            Created by {event.created_by_name || event.created_by} • {formatDisplay(event.created_at)}
          </Typography>
          {event.updated_by && (
            <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
              Updated by {event.updated_by_name || event.updated_by} • {formatDisplay(event.updated_at)}
            </Typography>
          )}
        </Box>
      </DialogContent>

      {isOwner && !isTerminal && (
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <RequirePermission module={MODULES.TEST_CALENDAR} operation="delete">
            <Button
              startIcon={<Trash2 size={16} />}
              color="error"
              size="small"
              onClick={() => {
                if (window.confirm('Delete this event?')) {
                  onDelete(event.id);
                  onClose();
                }
              }}
            >
              Delete
            </Button>
          </RequirePermission>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <RequirePermission module={MODULES.TEST_CALENDAR} operation="write">
              {availableTransitions.length > 0 && !showStatusChange && (
                <Button
                  startIcon={<ArrowRightLeft size={16} />}
                  size="small"
                  onClick={() => { setShowStatusChange(true); setShowExtend(false); setShowClose(false); }}
                >
                  Status
                </Button>
              )}
              {!showExtend && (event.status === 'IN_PROGRESS' || event.status === 'EXTENDED') && (
                <Button
                  startIcon={<Timer size={16} />}
                  size="small"
                  onClick={() => { setShowExtend(true); setShowClose(false); setShowStatusChange(false); }}
                >
                  Extend
                </Button>
              )}
              {!showClose && !isTerminal && (
                <Button
                  startIcon={<CheckCircle size={16} />}
                  size="small"
                  color="success"
                  onClick={() => { setShowClose(true); setShowExtend(false); setShowStatusChange(false); }}
                >
                  Close
                </Button>
              )}
              <Button
                startIcon={<Pencil size={16} />}
                variant="contained"
                size="small"
                onClick={() => onEdit(event)}
                sx={{ backgroundColor: '#01426A', '&:hover': { backgroundColor: '#002F5F' } }}
              >
                Edit
              </Button>
            </RequirePermission>
          </Box>
        </DialogActions>
      )}
    </Dialog>
  );
};

export { EventDetailDialog };
export default EventDetailDialog;
